/**
 * ToastContext — lightweight toast notification system
 *
 * Usage:
 *   const { addToast } = useToast();
 *   addToast({ message: 'Saved!', type: 'success' });
 *   addToast({ message: 'Update available', type: 'info', action: { label: 'Refresh', onClick: fn } });
 */
import { createContext, useContext, useState, useCallback, useRef } from 'react';
import ToastContainer from './ToastContainer';

const ToastContext = createContext(null);

const DURATIONS = { success: 4000, info: 4000, error: 6000 };

let nextId = 0;

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.map((t) => t.id === id ? { ...t, dismissing: true } : t));
  }, []);

  const addToast = useCallback(({ message, type = 'info', action = null, duration }) => {
    const id = ++nextId;
    const ms = duration ?? DURATIONS[type] ?? 4000;

    setToasts((prev) => [...prev, { id, message, type, action }]);

    if (ms > 0) {
      timersRef.current[id] = setTimeout(() => dismissToast(id), ms);
    }

    return id;
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export { ToastProvider, useToast };
export default ToastContext;
